import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, download, fetchBlobUrl } from "../api";
import { fmtINR, fmtDate } from "../fmt";

/**
 * Tax / ITR screen. Upload Form-16 + AIS, then view the ITR-2 computation for
 * an assessment year under BOTH regimes (beneficial one highlighted), and
 * download a CA-style computation sheet + a DRAFT ITD ITR-2 JSON.
 *
 * A persistent DRAFT banner rides on the whole screen: the tax constants are
 * pending CA verification and nothing here is for filing as-is.
 */

const AYS = ["AY2025-26", "AY2026-27"];

interface RegimeResult {
  regime: "old" | "new";
  standardDeduction: number;
  salaryIncome: number;
  housePropertyIncome: number;
  otherSourcesIncome: number;
  deductions: { chapterVIATotal: number };
  grossTotalIncome: number;
  normalTaxableIncome: number;
  specialRate: { stcg111A: number; ltcg112ATaxable: number; taxOnSpecial: number };
  totalIncome: number;
  taxOnNormal: number;
  taxOnSpecial: number;
  rebate87A: number;
  surcharge: number;
  cess: number;
  totalTaxLiability: number;
  tds: number;
  balancePayable: number;
}

interface Computation {
  ay: string;
  fy: string;
  old: RegimeResult;
  new: RegimeResult;
  beneficialRegime: "old" | "new";
  constantsStatus: string;
  disclaimer: string;
  sources: {
    salary: string;
    houseProperty: string;
    otherSources: string;
    chapterVIA: string;
    capitalGains: string;
    tds: string;
    notes: string[];
  };
  documentCount: number;
}

interface TaxDoc {
  id: string;
  kind: string;
  documentId: string;
  title: string;
  parsed: Record<string, unknown>;
}

function Banner() {
  return (
    <div
      style={{
        background: "#fff4f4",
        border: "1px solid #e0b4b4",
        color: "#a11",
        padding: "0.6rem 0.9rem",
        borderRadius: 6,
        fontWeight: 600,
        marginBottom: "1rem",
      }}
    >
      DRAFT — tax constants are pending CA verification. This computation is for
      PREPARATION only; it is NOT authoritative and NOT for filing as-is. Validate
      the generated JSON in the official ITD utility before any upload.
    </div>
  );
}

function UploadCard({
  title,
  hint,
  accept,
  ay,
  endpoint,
  onDone,
}: {
  title: string;
  hint: string;
  accept: string;
  ay: string;
  endpoint: string;
  onDone: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const onFile = async (file: File) => {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ay", ay);
      const res = await api.post<{ kind: string; ay: string | null; manualEntryNeeded: boolean }>(endpoint, fd);
      setMsg(
        `Parsed ${res.kind}${res.ay ? ` for ${res.ay}` : ""}.` +
          (res.manualEntryNeeded ? " Some fields could not be read — verify/enter manually." : " Saved.")
      );
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="card" style={{ flex: "1 1 260px" }}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <p className="muted" style={{ marginTop: 0 }}>{hint}</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={busy}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onFile(f);
        }}
      />
      {busy && <div className="muted">Uploading…</div>}
      {msg && <div style={{ color: "#187", marginTop: "0.4rem" }}>{msg}</div>}
      {err && <div className="error-text">{err}</div>}
    </div>
  );
}

function num(n: number): string {
  return fmtINR(n);
}

interface ItrRow {
  ay: string;
  documentId: string;
  title: string;
  parsed: {
    fy?: string;
    filed?: string | null;
    salary?: number | null;
    cap_gains?: number | null;
    gti?: number | null;
    chapVIA?: number | null;
    total_income?: number | null;
    total_tax?: number | null;
    verified?: boolean;
  };
}

/** Year-wise item-wise view of every filed ITR (actuals), with the source PDF
 *  one click away. Figures are auto-extracted from the filed returns; the
 *  `verified` flag marks the years whose GTI / deductions / total income
 *  reconcile exactly — the rest are shown for the user (CA) to verify. */
function ItrHistory() {
  const [rows, setRows] = useState<ItrRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<ItrRow[]>("/api/tax/itr-history")
      .then(setRows)
      .catch((e) => setErr(e instanceof ApiError ? e.message : "failed to load ITR history"));
  }, []);

  const openPdf = async (documentId: string) => {
    try {
      const { url } = await fetchBlobUrl(`/api/documents/${documentId}/file`);
      window.open(url, "_blank", "noopener");
    } catch {
      /* document may have been removed */
    }
  };

  const cell = (v: number | null | undefined) =>
    v === null || v === undefined ? <span className="muted">—</span> : num(v);

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 style={{ marginTop: 0 }}>ITR History — filed returns (item-wise)</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        Figures auto-extracted from the actual filed ITR PDFs. A ✓ marks years whose gross total income,
        Chapter&nbsp;VI-A deductions and total income reconcile exactly; open the PDF to verify any figure.
      </p>
      {err && <div className="error-text">{err}</div>}
      {!rows && !err && <div className="muted">Loading…</div>}
      {rows && rows.length === 0 && <p className="muted">No ITRs uploaded yet.</p>}
      {rows && rows.length > 0 && (
        <div className="table-wrap" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.92em" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>FY</th>
                <th style={{ textAlign: "left" }}>AY</th>
                <th style={{ textAlign: "right" }}>Salary</th>
                <th style={{ textAlign: "right" }}>Cap. gains</th>
                <th style={{ textAlign: "right" }}>Gross total inc.</th>
                <th style={{ textAlign: "right" }}>Ch. VI-A</th>
                <th style={{ textAlign: "right" }}>Total income</th>
                <th style={{ textAlign: "right" }}>Total tax</th>
                <th style={{ textAlign: "left" }}>Filed</th>
                <th style={{ textAlign: "left" }}>PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ay + r.documentId}>
                  <td style={{ textAlign: "left" }}>
                    {r.parsed.fy ?? "—"}{" "}
                    {r.parsed.verified ? (
                      <span title="reconciles exactly" style={{ color: "#2b8a2b" }}>✓</span>
                    ) : (
                      <span title="verify against PDF" style={{ color: "#a60" }}>⚠</span>
                    )}
                  </td>
                  <td style={{ textAlign: "left" }}>{r.ay.replace("AY", "")}</td>
                  <td style={{ textAlign: "right" }}>{cell(r.parsed.salary)}</td>
                  <td style={{ textAlign: "right" }}>{cell(r.parsed.cap_gains)}</td>
                  <td style={{ textAlign: "right" }}>{cell(r.parsed.gti)}</td>
                  <td style={{ textAlign: "right" }}>{cell(r.parsed.chapVIA)}</td>
                  <td style={{ textAlign: "right", fontWeight: 600 }}>{cell(r.parsed.total_income)}</td>
                  <td style={{ textAlign: "right" }}>{cell(r.parsed.total_tax)}</td>
                  <td style={{ textAlign: "left" }} className="muted">{r.parsed.filed ?? "—"}</td>
                  <td style={{ textAlign: "left" }}>
                    <button
                      type="button"
                      className="link-button"
                      style={{ background: "none", border: "none", color: "#25e", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                      onClick={() => void openPdf(r.documentId)}
                    >
                      open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RegimeColumn({ r, beneficial }: { r: RegimeResult; beneficial: boolean }) {
  return (
    <th
      style={{
        textAlign: "right",
        // translucent so it works on light AND dark backgrounds
        background: beneficial ? "rgba(43,138,43,0.16)" : undefined,
        border: beneficial ? "2px solid #3aa03a" : undefined,
      }}
    >
      <span style={{ color: "var(--text, #e8e8e8)" }}>{r.regime === "old" ? "Old regime" : "New regime"}</span>
      {beneficial && <div style={{ color: "#3aa03a", fontSize: "0.8em", fontWeight: 600 }}>✓ beneficial</div>}
    </th>
  );
}

function ComputationView({ c }: { c: Computation }) {
  const rows: { label: string; get: (r: RegimeResult) => number; bold?: boolean }[] = [
    { label: "Salary income (after std deduction)", get: (r) => r.salaryIncome },
    { label: "  Standard deduction", get: (r) => r.standardDeduction },
    { label: "Income/(loss) from house property", get: (r) => r.housePropertyIncome },
    { label: "Income from other sources", get: (r) => r.otherSourcesIncome },
    { label: "Gross total income (normal heads)", get: (r) => r.grossTotalIncome, bold: true },
    { label: "Less: Chapter VI-A deductions", get: (r) => r.deductions.chapterVIATotal },
    { label: "Normal-rate taxable income", get: (r) => r.normalTaxableIncome, bold: true },
    { label: "STCG u/s 111A", get: (r) => r.specialRate.stcg111A },
    { label: "LTCG u/s 112A (taxable)", get: (r) => r.specialRate.ltcg112ATaxable },
    { label: "Total income", get: (r) => r.totalIncome, bold: true },
    { label: "Tax on normal income (slabs)", get: (r) => r.taxOnNormal },
    { label: "Tax on special-rate income", get: (r) => r.taxOnSpecial },
    { label: "Less: Rebate u/s 87A", get: (r) => r.rebate87A },
    { label: "Add: Surcharge", get: (r) => r.surcharge },
    { label: "Add: Cess (4%)", get: (r) => r.cess },
    { label: "Total tax liability", get: (r) => r.totalTaxLiability, bold: true },
    { label: "Less: TDS", get: (r) => r.tds },
    { label: "Balance payable / (refund)", get: (r) => r.balancePayable, bold: true },
  ];
  const oldBen = c.beneficialRegime === "old";
  const newBen = c.beneficialRegime === "new";

  return (
    <div className="table-wrap" style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left" }}>Head</th>
            <RegimeColumn r={c.old} beneficial={oldBen} />
            <RegimeColumn r={c.new} beneficial={newBen} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} style={row.bold ? { fontWeight: 700 } : undefined}>
              <td style={{ textAlign: "left" }}>{row.label}</td>
              <td style={{ textAlign: "right", color: "var(--text, #e8e8e8)",
                background: oldBen ? "rgba(43,138,43,0.10)" : undefined,
                fontWeight: oldBen ? 600 : undefined }}>{num(row.get(c.old))}</td>
              <td style={{ textAlign: "right", color: "var(--text, #e8e8e8)",
                background: newBen ? "rgba(43,138,43,0.10)" : undefined,
                fontWeight: newBen ? 600 : undefined }}>{num(row.get(c.new))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface EfileSession {
  assessmentYear: string;
  pan: string;
  status: "draft" | "client_pending" | "client_added" | "filed" | "verified" | "error";
  acknowledgement: string | null;
  transactionId: string | null;
  verifiedAt: string | null;
  verifyMode: string | null;
  env: string;
  lastError: string | null;
}
interface EfileStatus {
  configured: boolean;
  env: string;
  session: EfileSession | null;
}

/** In-app ITR e-filing via the Sandbox ERI channel: add taxpayer as client
 * (OTP consent) -> file -> e-verify. No portal password; filing is explicit. */
function EfileWizard({ ay }: { ay: string }) {
  const [status, setStatus] = useState<EfileStatus | null>(null);
  const [pan, setPan] = useState("");
  const [otp, setOtp] = useState("");
  const [everifyMode, setEverifyMode] = useState<"evc" | "aadhaar_otp">("aadhaar_otp");
  const [everifyOtp, setEverifyOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .get<EfileStatus>(`/api/tax/efile/status?ay=${ay}`)
      .then((s) => {
        setStatus(s);
        if (s.session?.pan) setPan(s.session.pan);
      })
      .catch(() => setStatus(null));
  }, [ay]);
  useEffect(load, [load]);

  const call = async (fn: () => Promise<{ session: EfileSession; message?: string }>) => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await fn();
      setMsg(res.message ?? "done");
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "e-filing action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;
  const s = status.session;
  const st = s?.status ?? "draft";

  if (!status.configured) {
    return (
      <div className="card" style={{ marginTop: "1rem" }}>
        <h3 style={{ marginTop: 0 }}>File in-app (Sandbox ERI)</h3>
        <p className="muted" style={{ margin: 0 }}>
          In-app e-filing is not configured yet (Sandbox API key/secret not set). Download the ITR
          JSON above and file it yourself on the portal for now.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: "1rem" }}>
      <h3 style={{ marginTop: 0 }}>
        File in-app via Sandbox (ERI){" "}
        <span className="badge" style={{ background: status.env === "live" ? "#c0392b" : "#888" }}>
          {status.env === "live" ? "LIVE" : "TEST"}
        </span>
      </h3>
      <p className="muted" style={{ marginTop: 0 }}>
        No income-tax portal password is used — the taxpayer consents by OTP and e-verifies
        themselves. Filing is explicit and irreversible.
      </p>

      <ol style={{ margin: "0 0 0.5rem", paddingLeft: "1.2rem" }}>
        <li style={{ opacity: st === "client_added" || st === "filed" || st === "verified" ? 0.5 : 1 }}>
          <strong>Add taxpayer &amp; consent:</strong>
          {st === "draft" || st === "error" ? (
            <div className="toolbar" style={{ marginTop: "0.3rem" }}>
              <input
                placeholder="Taxpayer PAN"
                value={pan}
                maxLength={10}
                onChange={(e) => setPan(e.target.value.toUpperCase())}
                style={{ textTransform: "uppercase" }}
              />
              <button
                type="button"
                disabled={busy || !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)}
                onClick={() => call(() => api.post(`/api/tax/efile/add-client`, { ay, pan }))}
              >
                {busy ? "…" : "Send OTP"}
              </button>
            </div>
          ) : (
            <span className="muted"> {s?.pan} — {st === "client_pending" ? "OTP sent" : "consented ✓"}</span>
          )}
        </li>

        {st === "client_pending" && (
          <li>
            <strong>Enter taxpayer OTP:</strong>
            <div className="toolbar" style={{ marginTop: "0.3rem" }}>
              <input placeholder="OTP" value={otp} onChange={(e) => setOtp(e.target.value)} />
              <button
                type="button"
                disabled={busy || otp.length < 4}
                onClick={() => call(() => api.post(`/api/tax/efile/confirm-otp`, { ay, otp }))}
              >
                {busy ? "…" : "Confirm consent"}
              </button>
            </div>
          </li>
        )}

        {(st === "client_added" || st === "error") && s && (
          <li>
            <strong>File the return:</strong>
            <div className="toolbar" style={{ marginTop: "0.3rem" }}>
              <button
                type="button"
                className="primary"
                disabled={busy}
                onClick={() => {
                  if (
                    !confirm(
                      `File ITR for ${s.pan}, ${ay} via Sandbox (${status.env.toUpperCase()})? This is irreversible.`
                    )
                  )
                    return;
                  void call(() => api.post(`/api/tax/efile/file`, { ay }));
                }}
              >
                {busy ? "Filing…" : `File return (${status.env.toUpperCase()})`}
              </button>
            </div>
          </li>
        )}

        {st === "filed" && (
          <li>
            <strong>E-verify:</strong>
            <div className="toolbar" style={{ marginTop: "0.3rem", flexWrap: "wrap" }}>
              <select value={everifyMode} onChange={(e) => setEverifyMode(e.target.value as "evc" | "aadhaar_otp")}>
                <option value="aadhaar_otp">Aadhaar OTP</option>
                <option value="evc">EVC (bank/demat)</option>
              </select>
              <input
                placeholder="OTP/EVC (if received)"
                value={everifyOtp}
                onChange={(e) => setEverifyOtp(e.target.value)}
              />
              <button
                type="button"
                className="primary"
                disabled={busy}
                onClick={() =>
                  call(() =>
                    api.post(`/api/tax/efile/everify`, {
                      ay,
                      mode: everifyMode,
                      otp: everifyOtp || undefined,
                    })
                  )
                }
              >
                {busy ? "…" : "E-verify"}
              </button>
            </div>
          </li>
        )}
      </ol>

      {s?.acknowledgement && (
        <p style={{ margin: "0.3rem 0" }}>
          <strong>Acknowledgement:</strong> {s.acknowledgement}
        </p>
      )}
      {st === "verified" && (
        <p style={{ color: "#2a7", margin: "0.3rem 0" }}>
          ✓ Filed &amp; e-verified{s?.verifiedAt ? ` on ${fmtDate(s.verifiedAt)}` : ""}.
        </p>
      )}
      {msg && <div className="muted">{msg}</div>}
      {err && <div className="error-text">{err}</div>}
      {s?.lastError && st === "error" && <div className="error-text">Last error: {s.lastError}</div>}
    </div>
  );
}

export function Tax() {
  const [ay, setAy] = useState(AYS[AYS.length - 1]);
  const [comp, setComp] = useState<Computation | null>(null);
  const [docs, setDocs] = useState<TaxDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<Computation>(`/api/tax/${ay}/computation`),
      api.get<TaxDoc[]>(`/api/tax/${ay}/documents`),
    ])
      .then(([c, d]) => {
        setComp(c);
        setDocs(d);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "failed to load computation"))
      .finally(() => setLoading(false));
  }, [ay]);

  useEffect(load, [load]);

  return (
    <div>
      <h1>Tax / ITR</h1>
      <Banner />

      <ItrHistory />

      <div className="toolbar" style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
        {AYS.map((y) => (
          <button
            key={y}
            type="button"
            className={y === ay ? "primary" : undefined}
            onClick={() => setAy(y)}
          >
            {y}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        <UploadCard
          title="Upload Form 16"
          hint="Part A (TDS) or Part B (salary), or an HBA home-loan interest cert. Both pre-2020 and TRACES layouts supported."
          accept="application/pdf,.pdf"
          ay={ay}
          endpoint="/api/tax/form16"
          onDone={load}
        />
        <UploadCard
          title="Upload AIS"
          hint="Annual Information Statement (PDF or JSON). Used for cross-check of interest / dividend / capital gains."
          accept="application/pdf,application/json,.pdf,.json"
          ay={ay}
          endpoint="/api/tax/ais"
          onDone={load}
        />
      </div>

      {error && <div className="error-text">{error}</div>}
      {loading && <div className="muted">Loading…</div>}

      {comp && !loading && (
        <>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.8rem", flexWrap: "wrap" }}>
            <button type="button" onClick={() => void download(`/api/tax/${ay}/computation.xlsx`, `computation-${ay}.xlsx`)}>
              Download computation (xlsx)
            </button>
            <button type="button" onClick={() => void download(`/api/tax/${ay}/itr.json`, `ITR2-${ay}-DRAFT.json`)}>
              Download ITR JSON (draft)
            </button>
          </div>
          <p className="muted" style={{ marginTop: 0 }}>
            Or file in-app below (Sandbox ERI). To file manually instead, upload the downloaded JSON at{" "}
            <a href="https://www.incometax.gov.in" target="_blank" rel="noreferrer">
              incometax.gov.in
            </a>{" "}
            after validating it in the ITD utility.
          </p>

          <EfileWizard ay={ay} />

          <ComputationView c={comp} />

          <div className="card" style={{ marginTop: "1rem" }}>
            <h3 style={{ marginTop: 0 }}>Where each figure came from</h3>
            <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
              <li>Salary: {comp.sources.salary}</li>
              <li>House property: {comp.sources.houseProperty}</li>
              <li>Other sources: {comp.sources.otherSources}</li>
              <li>Chapter VI-A: {comp.sources.chapterVIA}</li>
              <li>Capital gains: {comp.sources.capitalGains}</li>
              <li>TDS: {comp.sources.tds}</li>
            </ul>
            {comp.sources.notes.length > 0 && (
              <>
                <h4 style={{ marginBottom: "0.3rem" }}>Verify / not available</h4>
                <ul style={{ margin: 0, paddingLeft: "1.2rem", color: "#a60" }}>
                  {comp.sources.notes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div className="card" style={{ marginTop: "1rem" }}>
            <h3 style={{ marginTop: 0 }}>Source documents ({docs.length})</h3>
            {docs.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>No Form-16 / AIS uploaded for {ay} yet.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                {docs.map((d) => (
                  <li key={d.id}>
                    <span className="badge">{d.kind}</span>{" "}
                    <button
                      type="button"
                      className="link-button"
                      style={{ background: "none", border: "none", color: "#25e", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                      onClick={async () => {
                        try {
                          const { url } = await fetchBlobUrl(`/api/documents/${d.documentId}/file`);
                          window.open(url, "_blank", "noopener");
                        } catch {
                          /* ignore — document may have been removed */
                        }
                      }}
                    >
                      {d.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
